import { cn } from '@heroui/react'
import Mention from '@tiptap/extension-mention'
import { EditorContent, useEditor } from '@tiptap/react'
import { ReactRenderer } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Button } from 'components/ui/Button'
import { Send } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useFilter } from 'react-aria-components'
import tippy from 'tippy.js'
import { MentionList } from './MentionList'

export function TipTapNoteEditor({ onSubmit, orgMembers, placeholder = 'Add a note...' }) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isEmpty, setIsEmpty] = useState(true)
  const { contains } = useFilter({
    sensitivity: 'base',
  })

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        paragraph: {
          HTMLAttributes: {
            class: 'mb-2',
          },
        },
      }),
      Mention.configure({
        HTMLAttributes: {
          class: 'mention',
        },
        suggestion: {
          items: ({ query }) => {
            return orgMembers.filter(member =>
              contains(member.first_name, query)
              || contains(member.last_name, query)
              || contains(member.member_email, query),
            )
              .slice(0, 5)
          },
          render: () => {
            let component
            let popup

            return {
              onStart: (props) => {
                component = new ReactRenderer(MentionList, {
                  props,
                  editor: props.editor,
                })

                popup = tippy('body', {
                  getReferenceClientRect: props.clientRect,
                  appendTo: () => document.body,
                  content: component.element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'bottom-start',
                })
              },

              onUpdate(props) {
                component.updateProps(props)

                popup[0].setProps({
                  getReferenceClientRect: props.clientRect,
                })
              },

              onKeyDown(props) {
                if (props.event.key === 'Escape') {
                  popup[0].hide()
                  return true
                }

                return component.ref?.onKeyDown(props)
              },

              onExit() {
                popup[0].destroy()
                component.destroy()
              },
            }
          },
        },
      }),
    ],
    content: '',
    onUpdate({ editor }) {
      setIsEmpty(editor.isEmpty)
    },
  })

  const handleSubmit = useCallback(async () => {
    if (!editor || isEmpty || isSubmitting)
      return

    const content = editor.getHTML()
    if (!content.trim() || content === '<p></p>')
      return

    setIsSubmitting(true)
    try {
      await onSubmit(content)
      editor.commands.clearContent()
    }
    catch (error) {
      console.error('Failed to submit note:', error)
    }
    finally {
      setIsSubmitting(false)
    }
  }, [editor, onSubmit, isSubmitting, isEmpty])

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      handleSubmit()
    }
  }, [handleSubmit])

  useEffect(() => {
    if (!editor)
      return

    const element = editor.view.dom
    element.addEventListener('keydown', handleKeyDown)
    return () => element.removeEventListener('keydown', handleKeyDown)
  }, [editor, handleKeyDown])

  if (!editor) {
    return null
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div
        className={cn(
          'prose prose-sm max-w-none cursor-text p-3',
          `
            focus-within:ring-2 focus-within:ring-blue-500
            focus-within:ring-offset-2
          `,
          'max-h-[300px] min-h-[100px] overflow-y-auto',
        )}
        onClick={() => {
          if (editor && !editor.isFocused) {
            editor.commands.focus('end')
          }
        }}
      >
        <EditorContent
          editor={editor}
          placeholder={placeholder}
        />
      </div>
      <div className={`
        flex items-center justify-between border-t border-gray-100 px-3 py-2
      `}
      >
        <span className="text-xs text-gray-500">
          Press Cmd+Enter to submit
        </span>
        <Button
          size="sm"
          isDisabled={isEmpty || isSubmitting}
          onPress={handleSubmit}
          isLoading={isSubmitting}
        >
          <Send size={14} />
          Send
        </Button>
      </div>
      <style jsx global>
        {`
        .mention {
          background-color: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
          border-radius: 0.25rem;
          padding: 0.125rem 0.25rem;
          font-weight: 500;
        }
        
        .ProseMirror {
          outline: none;
        }
        
        .ProseMirror p {
          margin: 0;
        }
        
        .ProseMirror p + p {
          margin-top: 0.5rem;
        }
        
        .ProseMirror:empty::before {
          content: attr(placeholder);
          float: left;
          color: #9ca3af;
          pointer-events: none;
          height: 0;
        }
      `}
      </style>
    </div>
  )
}
